import httpStore from "@/api/httpStore.js";
import { createQueryString } from "./OrdersService.js";
export function getCustomers(filters) {
  const newFilters = Object.entries(filters)
    .map(([key, val]) => {
      return `${encodeURIComponent(key)}=${encodeURIComponent(val)}`;
    })
    .join("&");
  return httpStore.get(`/customers/set?${newFilters}`).then(res => {
    return res.data;
  });
}

export function getCustomer(customerId) {
  return httpStore
    .get(`/customers/single/${customerId}`)
    .then(res => {
      return res.data;
    })
    .catch(e => {
      throw e;
    });
}

export function saveCustomerGeneralInfo(customerGeneralInfo) {
  return httpStore
    .post(`/customers/update/${customerGeneralInfo.id}`, customerGeneralInfo)
    .then(res => {
      return res.data;
    })
    .catch(e => {
      throw e;
    });
}

export function getPriceLevels() {
  return [
    {
      label: "Online",
      value: "online"
    },
    {
      label: "Repair Shop",
      value: "repair_shop"
    },
    {
      label: "Wholesale",
      value: "wholesale"
    },
    {
      label: "Export",
      value: "precio4",
      permission: "price_precio4"
    },
    {
      label: "GrupoNKR",
      value: "precio5",
      permission: "price_precio5"
    },
    {
      label: "WH2",
      value: "precio6",
      permission: "price_precio6"
    }
  ];
}

export function getCustomerCart(customerId) {
  return httpStore
    .get(`/customers/cart/${customerId}`)
    .then(res => {
      return res.data;
    })
    .catch(e => {
      throw e;
    });
}

export function updateCartItems(customerId, cartUpdates) {
  return httpStore
    .post(`/customers/cart_update/${customerId}`, { cart: cartUpdates })
    .then(res => {
      return res.data;
    })
    .catch(e => {
      throw e;
    });
}

export function getCustomerResaleTax(email, infoautoId) {
  return httpStore
    .post(`/customers/retrieve_resale_tax`, {
      email: email,
      infoautoId: infoautoId
    })
    .then(res => {
      return res.data;
    });
}

export function createResaleTax(resaleTaxData) {
  return httpStore.post("/customers/resale_tax", resaleTaxData).then(res => {
    return res.data;
  });
}

export function deleteResaleTax(resaleTaxId, reason) {
  const query = reason ? `?reason=${encodeURIComponent(reason)}` : "";
  return httpStore
    .delete(`/customers/resale_tax/${resaleTaxId}${query}`)
    .then(res => {
      return res.data;
    });
}

export function getResaleTaxDocuments(filters) {
  return httpStore
    .get(`/resale_tax?${createQueryString(filters)}`)
    .then(res => {
      return res.data;
    });
}

export function validateResaleTax(id, notes) {
  const query = notes ? `?validation_notes=${encodeURIComponent(notes)}` : "";
  return httpStore.get(`/resale_tax/validate/${id}${query}`).then(res => {
    return res.data;
  });
}

export function validateResaleTaxAI(id) {
  return httpStore.get(`/resale_tax/validate_ai/${id}`).then(res => {
    return res.data;
  });
}

export function validateAllPendingResaleTaxAI() {
  return httpStore.get(`/resale_tax/validate_all_pending`).then(res => {
    return res.data;
  });
}

/**
 * Get all web customers linked to an infoauto_id
 * @param {string} infoautoId - The infoauto customer ID
 * @returns {Promise} Array of customers with matching infoauto_id
 */
export function getCustomersByInfoautoId(infoautoId) {
  return httpStore
    .get(`/customers/set?infoauto_id=${encodeURIComponent(infoautoId)}`)
    .then(res => {
      return res.data;
    })
    .catch(e => {
      console.error("Error fetching customers by infoauto_id:", e);
      throw e;
    });
}

/**
 * Search web customers by email or name
 * @param {string} search - Search term (email, name, surname)
 * @returns {Promise} Array of matching customers
 */
export function searchWebCustomers(search) {
  return httpStore
    .get(`/customers/set?search=${encodeURIComponent(search)}&limit=50`)
    .then(res => {
      return res.data;
    })
    .catch(e => {
      console.error("Error searching customers:", e);
      throw e;
    });
}

/**
 * Create a new web customer and link to infoauto
 * @param {object} customerData - Customer information
 * @returns {Promise} Created customer
 */
export function createLinkedCustomer(customerData) {
  return httpStore
    .post(`/customers/create`, customerData)
    .then(res => {
      return res.data;
    })
    .catch(e => {
      console.error("Error creating linked customer:", e);
      throw e;
    });
}

/**
 * Update customer's infoauto_id link
 * @param {number} customerId - Web customer ID
 * @param {string} infoautoId - Infoauto customer ID to link (or empty string to unlink)
 * @returns {Promise} Updated customer
 */
export function linkCustomerToInfoauto(customerId, infoautoId) {
  return httpStore
    .post(`/customers/update/${customerId}`, { infoautoId: infoautoId })
    .then(res => {
      return res.data;
    })
    .catch(e => {
      console.error("Error linking customer to infoauto:", e);
      throw e;
    });
}

export function getCustomerOrderStats(infoautoId) {
  return httpStore
    .get(`/customers/order_stats/${encodeURIComponent(infoautoId)}`)
    .then(res => res.data);
}

/**
 * Save one of the order's current values as the default of its infoauto
 * customer (the account).
 * @param {string} orderNumber
 * @param {"shipping_preference"|"payment_type"} field
 * @returns {Promise} Updated customer
 */
export function saveCustomerOrderDefault(orderNumber, field) {
  return httpStore
    .post(`/customers/order_default`, { orderNumber, field })
    .then(res => res.data);
}

// VINs of the customer's other orders: they do not change while one order is
// edited, and the order reloads (and asks again) after every save
const VINS_TTL_MS = 5 * 60 * 1000;
const vinsCache = new Map();

/**
 * VINs used in the customer's previous orders, most recent first. Kept in
 * memory for a few minutes per customer and order (see VINS_TTL_MS).
 * @param {string} infoautoId
 * @param {string} [excludeOrderNumber] - order being edited, left out
 * @returns {Promise<Array<{vin: string, lastUsed: string, orders: string}>>}
 */
export function getCustomerVins(infoautoId, excludeOrderNumber) {
  const key = `${infoautoId}|${excludeOrderNumber || ""}`;
  const cached = vinsCache.get(key);
  if (cached && Date.now() - cached.at < VINS_TTL_MS) {
    return cached.request;
  }
  const query = excludeOrderNumber
    ? `?excludeOrderNumber=${encodeURIComponent(excludeOrderNumber)}`
    : "";
  const request = httpStore
    .get(`/customers/vins/${encodeURIComponent(infoautoId)}${query}`)
    .then(res => res.data)
    .catch(e => {
      // A failure is asked again next time
      vinsCache.delete(key);
      throw e;
    });
  vinsCache.set(key, { at: Date.now(), request });
  return request;
}

/**
 * Web customers not connected to an infoauto customer, with the suggested
 * infoauto customers (same email, orders billed to it). The backend matches
 * against a cached list of every infoauto customer; `refresh` reloads it.
 * @param {{page:number, limit:number, search:string, scope:"suggested"|"all", refresh?:boolean}} filters
 */
export function getInfoautoLinkSuggestions(filters) {
  const params = new URLSearchParams({
    page: filters.page,
    limit: filters.limit,
    search: filters.search || "",
    scope: filters.scope || "suggested",
    refresh: filters.refresh ? "1" : "0"
  });
  return httpStore
    .get(`/customers/infoauto_link_suggestions?${params.toString()}`)
    .then(res => res.data);
}

/**
 * Infoauto customers found by a web customer's phone (searched on demand).
 */
export function getInfoautoPhoneCandidates(customerId) {
  return httpStore
    .get(`/customers/infoauto_phone_candidates/${customerId}`)
    .then(res => res.data);
}

/**
 * Accept or reject a suggested link between a web customer and an infoauto
 * customer. Rejected suggestions are not offered again.
 * @param {"accepted"|"rejected"} decision
 * @param {string[]} reasons
 */
export function decideInfoautoLink(customerId, infoautoId, decision, reasons) {
  return httpStore
    .post(`/customers/infoauto_link`, {
      customerId,
      infoautoId,
      decision,
      reasons: (reasons || []).join(",")
    })
    .then(res => res.data);
}

/**
 * Web customers of an infoauto customer: linked ones with their activity and
 * unlinked ones that look like its users.
 */
export function getInfoautoWebCustomers(infoautoId) {
  return httpStore
    .get(`/customers/infoauto_web_customers/${encodeURIComponent(infoautoId)}`)
    .then(res => res.data);
}
